import React from "react";
import { Modal } from "rsuite";
import { Translation } from "react-i18next";
import { connect } from "react-redux";
import toast from "react-hot-toast";
import { FaGift } from "react-icons/fa";
import { GiSparkles } from "react-icons/gi";

import { UserService } from "_services";
import Emitter from "_actions/emitter";
import i18n from "i18n";
import { GoldButton, CloseButton, DividerDiamond } from "./BalanceModal";

const AMOUNT_STEP = 10;
const MAX_AMOUNT = 999999;
const MAX_MESSAGE_LENGTH = 300;

const BonusRequestModal = (props) => {
  const [message, setMessage] = React.useState("");
  const [amount, setAmount] = React.useState("");
  const [loading, setLoading] = React.useState(false);

  const resetState = () => {
    setMessage("");
    setAmount("");
    setLoading(false);
  };

  React.useEffect(() => {
    if (!props.isActive) {
      resetState();
    }
  }, [props.isActive]);

  const handleAmountChange = (e) => {
    let value = e.target.value.replace(/[^0-9.]/g, "");
    const firstDot = value.indexOf(".");
    if (firstDot !== -1) {
      value =
        value.slice(0, firstDot + 1) +
        value.slice(firstDot + 1).replace(/\./g, "");
    }
    if (parseFloat(value) > MAX_AMOUNT) {
      value = String(MAX_AMOUNT);
    }
    setAmount(value);
  };

  const handleStep = (delta) => {
    const current = parseFloat(amount) || 0;
    const next = Math.min(MAX_AMOUNT, Math.max(0, current + delta));
    setAmount(Number.isInteger(next) ? String(next) : next.toFixed(2));
  };

  const numericAmount = parseFloat(amount) || 0;
  const canSubmit = numericAmount > 0 && !loading;

  const handleClose = () => {
    Emitter.emit("BONUS_REQUEST_MODAL", false);
  };

  const handleSubmit = () => {
    if (!canSubmit) return;
    setLoading(true);
    UserService.CREATE_BONUS_REQUEST(message, numericAmount)
      .then(() => {
        setLoading(false);
        toast.success(i18n.t("BONUS_REQUEST_MODAL_SUCCESS"));
        Emitter.emit("BONUS_REQUEST_CREATED");
        handleClose();
      })
      .catch((err) => {
        setLoading(false);
        const failText = err?.response?.data?.failText?.[0];
        toast.error(failText || i18n.t("BONUS_REQUEST_MODAL_ERROR"));
      });
  };

  return (
    <Translation>
      {(t) => (
        <Modal
          onClose={handleClose}
          backdrop={true}
          className="custom-modal custom-modal-gold"
          open={props.isActive}
          size="xs"
        >
          <Modal.Header className="bg-secondary-500 border-0">
            <Modal.Title className="text-white" />
          </Modal.Header>
          <Modal.Body
            className="relative bg-secondary-500/95"
            style={{
              paddingTop: "0.5em",
              paddingBottom: "1em",
              marginTop: 0,
              maxHeight: "80dvh",
              overflowY: "auto",
              WebkitOverflowScrolling: "touch",
            }}
          >
            <CloseButton onClick={handleClose} />

            <div className="flex flex-col px-1 py-1">
              <div className="flex flex-col items-center mb-4">
                <FaGift className="text-primary-400 text-lg mb-1" />
                <div className="flex items-center gap-2">
                  <GiSparkles className="text-secondary-50/60 text-xs" />
                  <span className="bg-gradient-to-b from-primary-200 to-secondary-50 bg-clip-text text-transparent text-lg font-extrabold uppercase tracking-wide">
                    {t("BONUS_REQUEST_MODAL_TITLE")}
                  </span>
                  <GiSparkles className="text-secondary-50/60 text-xs" />
                </div>
                <span className="text-white text-[11px] uppercase tracking-[0.15em] mt-1 text-center">
                  {t("BONUS_REQUEST_MODAL_SUBTITLE")}
                </span>
              </div>

              <div className="w-full mb-2">
                <DividerDiamond />
              </div>

              <span className="text-white text-xs uppercase tracking-[0.15em] mb-2">
                {t("BONUS_REQUEST_MODAL_AMOUNT_LABEL")}
              </span>
              <div className="flex items-stretch gap-2 mb-4">
                <div className="flex-1 flex items-center rounded-xl border border-primary-400 bg-secondary-700/40 overflow-hidden">
                  <button
                    type="button"
                    onClick={() => handleStep(-AMOUNT_STEP)}
                    className="w-11 h-12 shrink-0 flex items-center justify-center text-primary-400 text-xl font-bold hover:bg-secondary-700/60"
                  >
                    −
                  </button>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={amount}
                    onChange={handleAmountChange}
                    placeholder="0.00"
                    className="flex-1 min-w-0 bg-transparent text-center text-white text-xl font-bold outline-none"
                    maxLength={6}
                  />
                  <button
                    type="button"
                    onClick={() => handleStep(AMOUNT_STEP)}
                    className="w-11 h-12 shrink-0 flex items-center justify-center text-primary-400 text-xl font-bold hover:bg-secondary-700/60"
                  >
                    +
                  </button>
                </div>
              </div>

              <span className="text-white text-xs uppercase tracking-[0.15em] mb-2">
                {t("BONUS_REQUEST_MODAL_MESSAGE_LABEL")}
              </span>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder={t("BONUS_REQUEST_MODAL_MESSAGE_PLACEHOLDER")}
                rows={3}
                maxLength={MAX_MESSAGE_LENGTH}
                className="w-full rounded-xl border border-primary-400 bg-primary-500/40 px-3.5 py-3 text-white placeholder-white/50 text-sm outline-none resize-none mb-4"
              />

              <GoldButton
                label={t("BONUS_REQUEST_MODAL_SUBMIT")}
                onClick={handleSubmit}
                disabled={!canSubmit}
                loading={loading}
                icon={<FaGift className="w-4 h-4" />}
              />
            </div>
          </Modal.Body>
        </Modal>
      )}
    </Translation>
  );
};

const mapStateToProps = (state) => {
  const { authentication } = state;
  const { user, wallet } = authentication;
  return {
    user,
    wallet,
  };
};

export default connect(mapStateToProps)(BonusRequestModal);
